import React from 'react';
import styled from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import {
  extractPlanFeatures,
  extractUsageMetrics,
} from './planDisplayUtils';

const MetricStack = styled.div`
  display: grid;
  gap: 3px;
  min-width: 132px;
  max-width: 168px;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-size: ${({ theme }) => theme.fsSm};
  line-height: 1.35;
`;

const MetricLabel = styled.span`
  color: ${({ theme }) => theme.textMuted};
  font-weight: 500;
  white-space: nowrap;
`;

const MetricValue = styled.span`
  color: ${({ theme, $unlimited }) => ($unlimited ? theme.textMuted : theme.text)};
  font-weight: 650;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const EmptyHint = styled.span`
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
`;

const FeatureBlock = styled.span`
  display: inline-block;
  min-width: 168px;
  max-width: 220px;
  cursor: ${({ $interactive }) => ($interactive ? 'help' : 'default')};
`;

const FeatureHeadline = styled.div`
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme, $empty }) => ($empty ? theme.textMuted : theme.text)};
  line-height: 1.35;
`;

const FeaturePreview = styled.div`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Overview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
`;

const OverviewBlock = styled.div``;

const OverviewLabel = styled.div`
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  color: ${({ theme }) => theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
`;

const FeatureList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 6px;
`;

const FeatureListItem = styled.li`
  position: relative;
  padding-left: 14px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme, $off }) => ($off ? theme.textMuted : theme.text)};
  line-height: 1.4;

  &::before {
    content: ${({ $off }) => ($off ? "'–'" : "'✓'")};
    position: absolute;
    left: 0;
    top: 0;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme, $off }) => ($off ? theme.textSubtle : theme.success || '#10977a')};
  }
`;

const TooltipWrap = styled.div`
  max-width: 280px;
  padding: 2px 0;
`;

const TooltipSection = styled.div`
  &:not(:first-child) {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
  }
`;

const TooltipHeading = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.82;
  margin-bottom: 6px;
`;

const TooltipLine = styled.div`
  font-size: 12px;
  line-height: 1.45;
  opacity: ${({ $muted }) => ($muted ? 0.72 : 1)};
`;

const FEATURE_PREVIEW_COUNT = 3;

function buildFeaturePreview(enabled) {
  if (!enabled.length) {
    return '';
  }
  const preview = enabled.slice(0, FEATURE_PREVIEW_COUNT).map((item) => item.label);
  const remaining = enabled.length - preview.length;
  if (remaining > 0) {
    return `${preview.join(', ')} +${remaining} more`;
  }
  return preview.join(', ');
}

function FeatureTooltipContent({ enabled, disabled }) {
  return (
    <TooltipWrap>
      {enabled.length > 0 && (
        <TooltipSection>
          <TooltipHeading>Included ({enabled.length})</TooltipHeading>
          {enabled.map((feature) => (
            <TooltipLine key={feature.entKey}>{feature.label}</TooltipLine>
          ))}
        </TooltipSection>
      )}
      {disabled.length > 0 && (
        <TooltipSection>
          <TooltipHeading>Not included ({disabled.length})</TooltipHeading>
          {disabled.map((feature) => (
            <TooltipLine key={feature.entKey} $muted>{feature.label}</TooltipLine>
          ))}
        </TooltipSection>
      )}
    </TooltipWrap>
  );
}

/** Compact usage limits for a plan table row. */
export function PlanUsageMetrics({ entitlements, planKind = 'instructor' }) {
  const metrics = extractUsageMetrics(entitlements, planKind);
  if (!metrics.length) {
    return <EmptyHint>—</EmptyHint>;
  }
  return (
    <MetricStack>
      {metrics.map((metric) => (
        <MetricRow key={metric.entKey}>
          <MetricLabel>{metric.label}</MetricLabel>
          <MetricValue $unlimited={metric.unlimited} className="tnum">
            {metric.display}
          </MetricValue>
        </MetricRow>
      ))}
    </MetricStack>
  );
}

/** Feature summary for a plan table row — headline + preview, full list on hover. */
export function PlanFeatureTags({ entitlements }) {
  const { enabled, disabled, disabledCount } = extractPlanFeatures(entitlements);
  const preview = buildFeaturePreview(enabled);
  const interactive = enabled.length > 0 || disabledCount > 0;

  if (!interactive) {
    return <EmptyHint>—</EmptyHint>;
  }

  const headline = enabled.length > 0
    ? `${enabled.length} feature${enabled.length === 1 ? '' : 's'} included`
    : 'No features included';

  const content = (
    <FeatureBlock as="span" $interactive={interactive}>
      <FeatureHeadline $empty={enabled.length === 0}>{headline}</FeatureHeadline>
      {preview && <FeaturePreview>{preview}</FeaturePreview>}
      {!preview && disabledCount > 0 && (
        <FeaturePreview>{disabledCount} locked on this tier</FeaturePreview>
      )}
    </FeatureBlock>
  );

  if (!interactive) {
    return content;
  }

  return (
    <Tooltip
      title={<FeatureTooltipContent enabled={enabled} disabled={disabled} />}
      arrow
      placement="top"
      enterTouchDelay={0}
    >
      {content}
    </Tooltip>
  );
}

/** Read-only allocation snapshot at the top of the plan edit dialog. */
export function PlanEntitlementOverview({ entitlements, planKind = 'instructor' }) {
  const metrics = extractUsageMetrics(entitlements, planKind);
  const { enabled, disabled } = extractPlanFeatures(entitlements);

  if (!metrics.length && !enabled.length && !disabled.length) {
    return null;
  }

  return (
    <Overview>
      {metrics.length > 0 && (
        <OverviewBlock>
          <OverviewLabel>Usage limits</OverviewLabel>
          <MetricStack style={{ maxWidth: '100%' }}>
            {metrics.map((metric) => (
              <MetricRow key={metric.entKey}>
                <MetricLabel>{metric.label}</MetricLabel>
                <MetricValue $unlimited={metric.unlimited} className="tnum">
                  {metric.display}
                </MetricValue>
              </MetricRow>
            ))}
          </MetricStack>
        </OverviewBlock>
      )}
      {(enabled.length > 0 || disabled.length > 0) && (
        <OverviewBlock>
          <OverviewLabel>Features</OverviewLabel>
          <FeatureList>
            {enabled.map((feature) => (
              <FeatureListItem key={feature.entKey}>{feature.label}</FeatureListItem>
            ))}
            {disabled.map((feature) => (
              <FeatureListItem key={feature.entKey} $off>{feature.label}</FeatureListItem>
            ))}
          </FeatureList>
        </OverviewBlock>
      )}
    </Overview>
  );
}
